import { useCallback } from 'react';
import { isEmpty, isNil, maxBy } from 'lodash';
import { MarketAdjustmentWritable, MarketComparisonWritable, ValuationAllocationModel } from 'api';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import { useFundInvestmentContext } from 'context';
import { FundInvestmentMarketAdjustmentCellData } from 'pages/FundInvestments/hooks/useFundInvestmentMarketAdjustmentSheet';
import {
  getComparisonsWithSingleWeighting,
  getUniqueComparisons,
  parsePublicCompsMarketComparison,
  saveFundInvestmentMarketAdjustment,
} from 'pages/FundInvestments/utils';
import { MarketDateDialogSaveProps } from 'pages/ValuationsAllocation/components/MarketDateDialog/MarketDateDialog.props';
import { CapIQCompanyData, TickersSymbolsByDate } from 'pages/ValuationsAllocation/types';
import { useAllocationValuesStore } from 'store';
import { dbShortDate, getArrayValue, getNumberValue, getObjectValue, getStringValue } from 'utilities';
import { UseFundInvestmentMarketAdjustmentActionsParams } from './useFundInvestmentMarketAdjustmentActions.types';

const useFundInvestmentMarketAdjustmentActions = (params: UseFundInvestmentMarketAdjustmentActionsParams) => {
  const {
    currentUniqueComparisons,
    evaluateSpreadsheets,
    marketAdjustmentFieldAttributes,
    marketAdjustmentRef,
    marketComparisonsRef,
    notes,
    notesHasChanged,
    prevMarketCurrencyRef,
    prevMarketDateRef,
    saveNotes,
    setMarketComparisonsForSheet,
    setPropertyUpdated,
    spreadsheets,
  } = params;

  const [, dispatch] = useStore() as UseStoreValues;

  // Allocation store
  const currentAllocationId = useAllocationValuesStore(state => state.currentAllocationId);

  // Fund investments context
  const { fundInvestmentSpreadsheet, fundInvestmentValuationAllocation } = useFundInvestmentContext();
  const {
    areAllCellsValid,
    cleanUnsavedChanges,
    currentAsOfDate,
    currentDeletedMarketAdjustments,
    currentDeletedMarketComparisons,
    currentMarketAdjustment,
    currentMarketComparisons,
    setArePendingChanges,
    setCurrentDeletedMarketAdjustments,
    setCurrentDeletedMarketComparisons,
    setHasValidationErrors,
    setIsValidating,
  } = fundInvestmentSpreadsheet;
  const {
    fundInvestmentByAllocation,
    marketCurrency,
    marketDate,
    setMarketCurrency,
    setMarketDate,
    setPublicCompsIndexesByDateParams,
    updateFundInvestmentDetails,
    updateValuation,
    valuationByAllocation,
  } = fundInvestmentValuationAllocation;

  const addComparison = useCallback(
    async (capIqComparisons: CapIQCompanyData[]) => {
      const { marketComparison: marketComparisonFieldAttributes } = marketAdjustmentFieldAttributes;

      const existingComparisons = getArrayValue(marketComparisonsRef.current);
      const uniqueExistingComparisons = getUniqueComparisons({
        comparisons: existingComparisons,
        date: marketDate,
        fallback: [],
      });

      // Initialize order from max existing
      let baseOrder = getNumberValue(maxBy(uniqueExistingComparisons, 'order')?.order);

      // Filter valid CapIQ comparisons
      const capIqValidComparisons = getArrayValue(capIqComparisons)
        .filter(comparison => comparison.valid_gpc_results)
        .map(comparison => {
          const { gpc: publicComp } = getObjectValue(comparison);

          // Increment order for each new comparison
          baseOrder += 1;

          return parsePublicCompsMarketComparison({
            fieldAttributes: marketComparisonFieldAttributes,
            order: baseOrder,
            publicComp,
          });
        })
        .filter(Boolean) as MarketComparisonWritable[];

      const uniqueAsOfDateComparisons = getUniqueComparisons({
        comparisons: capIqValidComparisons,
        date: getStringValue(currentAsOfDate),
        fallback: [],
      });

      const uniqueMarketDateComparisons = getUniqueComparisons({
        comparisons: capIqValidComparisons,
        date: marketDate,
        fallback: [],
      });

      const newComparisons = [...uniqueAsOfDateComparisons, ...uniqueMarketDateComparisons];

      if (newComparisons.length) {
        const updatedComparisons = getComparisonsWithSingleWeighting({
          comparisons: [...existingComparisons, ...newComparisons],
          marketDate,
        });

        setMarketComparisonsForSheet(updatedComparisons);

        // Prevent deletion of the current market adjustment
        setCurrentDeletedMarketAdjustments(null);
        // Mark changes as pending
        setArePendingChanges(true);

        // Evaluate spreadsheets, then re-run the parser in reverse order
        await evaluateSpreadsheets(spreadsheets);
      }
    },
    [
      currentAsOfDate,
      evaluateSpreadsheets,
      marketAdjustmentFieldAttributes,
      marketComparisonsRef,
      marketDate,
      setArePendingChanges,
      setCurrentDeletedMarketAdjustments,
      setMarketComparisonsForSheet,
      spreadsheets,
    ]
  );

  const removeComparison = useCallback(
    (cellData: FundInvestmentMarketAdjustmentCellData) => {
      const { capIqId, comparisonId } = getObjectValue(cellData);

      const filteredComparisons = getArrayValue(marketComparisonsRef.current).filter(
        comparison => comparison?.cap_iq_id !== capIqId
      );

      const updatedComparisons = getComparisonsWithSingleWeighting({
        comparisons: filteredComparisons,
        marketDate,
      });

      // If a comparison is removed, add it to the deleted market comparisons
      if (comparisonId) {
        setCurrentDeletedMarketComparisons(prevDeletedState =>
          Array.from(new Set([...getArrayValue(prevDeletedState), comparisonId]))
        );

        // Mark changes as pending
        setArePendingChanges(true);
      }

      // If all comparisons are removed, add the current market adjustment to be deleted
      if (isEmpty(updatedComparisons)) {
        setCurrentDeletedMarketAdjustments(prevDeletedState =>
          Array.from(new Set([...getArrayValue(prevDeletedState), getNumberValue(marketAdjustmentRef.current?.id)]))
        );
      }

      setMarketComparisonsForSheet(updatedComparisons);
    },
    [
      marketAdjustmentRef,
      marketComparisonsRef,
      marketDate,
      setArePendingChanges,
      setCurrentDeletedMarketAdjustments,
      setCurrentDeletedMarketComparisons,
      setMarketComparisonsForSheet,
    ]
  );

  const saveMarketAdjustment = useCallback(async () => {
    setIsValidating(true);

    if (!isEmpty(notes) && notesHasChanged) {
      saveNotes();
    }

    const hasInvalidCells = !isNil(areAllCellsValid) && !areAllCellsValid();

    setHasValidationErrors(hasInvalidCells);

    // If there are invalid cells, do not proceed with saving
    if (hasInvalidCells) {
      setIsValidating(false);
      return;
    }

    const updatedMarketAdjustments: MarketAdjustmentWritable[] = isEmpty(currentDeletedMarketAdjustments)
      ? [
        {
          ...(currentMarketAdjustment as MarketAdjustmentWritable),
          as_of_date: getStringValue(currentAsOfDate),
          market_comparisons: getArrayValue(currentMarketComparisons),
          deleted_market_comparisons: getArrayValue(currentDeletedMarketComparisons),
        },
      ]
      : []; // If there are deleted market adjustments, do not include the current market adjustment

    const updatedValutionMarketAdjustment = {
      ...valuationByAllocation,
      deleted_market_adjustments: getArrayValue(currentDeletedMarketAdjustments),
      market_adjustments: updatedMarketAdjustments,
      market_currency: marketCurrency,
      market_date: dbShortDate(marketDate),
    } as ValuationAllocationModel;

    // Save the updated market adjustment and fund investment details, update state, and clear unsaved changes
    await saveFundInvestmentMarketAdjustment({
      cleanUnsavedChanges,
      currentAllocationId,
      currentMarketAdjustment,
      dispatch,
      fundInvestmentByAllocation,
      setArePendingChanges,
      updatedValutionMarketAdjustment,
      updateFundInvestmentDetails,
      updateValuation,
    });

    setIsValidating(false);
  }, [
    areAllCellsValid,
    cleanUnsavedChanges,
    currentAllocationId,
    currentAsOfDate,
    currentDeletedMarketAdjustments,
    currentDeletedMarketComparisons,
    currentMarketAdjustment,
    currentMarketComparisons,
    dispatch,
    fundInvestmentByAllocation,
    marketCurrency,
    marketDate,
    notes,
    notesHasChanged,
    saveNotes,
    setArePendingChanges,
    setHasValidationErrors,
    setIsValidating,
    updateFundInvestmentDetails,
    updateValuation,
    valuationByAllocation,
  ]);

  const saveMarketDate = useCallback(
    (saveMarketDateParams: MarketDateDialogSaveProps) => {
      const { marketDate: updatedMarketDate, marketCurrency: updatedMarketCurrency } = saveMarketDateParams;

      const formattedMarketDate = getStringValue(dbShortDate(updatedMarketDate));

      const marketCurrencyChanged = prevMarketCurrencyRef.current !== updatedMarketCurrency;
      const marketDateChanged = prevMarketDateRef.current !== formattedMarketDate;

      // Update market date and/or market currency and refetch public comps by date
      if (marketCurrencyChanged || marketDateChanged) {
        const currentIndexesTickerSymbols = getArrayValue(currentUniqueComparisons)
          .filter(comparison => comparison.is_index)
          .map(comparison => getStringValue(comparison.ticker_symbol))
          .filter(Boolean);

        const currentPublicCompsTickerSymbols = getArrayValue(currentUniqueComparisons)
          .filter(comparison => !comparison.is_index)
          .map(comparison => getStringValue(comparison.ticker_symbol))
          .filter(Boolean);

        const currentIndexesTickerSymbolsByDate: TickersSymbolsByDate = {
          [formattedMarketDate]: currentIndexesTickerSymbols,
        };

        const currentPublicCompsTickerSymbolsByDate: TickersSymbolsByDate = {
          [formattedMarketDate]: currentPublicCompsTickerSymbols,
        };

        // Fetch both as of date and market date if market currency changed
        if (marketCurrencyChanged) {
          const formattedAsOfDate = getStringValue(dbShortDate(currentAsOfDate));

          currentIndexesTickerSymbolsByDate[formattedAsOfDate] = currentIndexesTickerSymbols;
          currentPublicCompsTickerSymbolsByDate[formattedAsOfDate] = currentPublicCompsTickerSymbols;
        }

        if (marketCurrencyChanged && marketDateChanged) {
          setPropertyUpdated('marketDateAndCurrency');
        } else if (marketCurrencyChanged) {
          setPropertyUpdated('currency');
        } else if (marketDateChanged) {
          setPropertyUpdated('marketDate');
        }

        setPublicCompsIndexesByDateParams({
          currency: updatedMarketCurrency,
          tickersSymbolsByDateAndType: {
            indexesByDate: currentIndexesTickerSymbolsByDate,
            publicCompsByDate: currentPublicCompsTickerSymbolsByDate,
          },
        });

        setMarketCurrency(updatedMarketCurrency);
        setMarketDate(updatedMarketDate);
      }
    },
    [
      currentAsOfDate,
      currentUniqueComparisons,
      prevMarketCurrencyRef,
      prevMarketDateRef,
      setMarketCurrency,
      setMarketDate,
      setPropertyUpdated,
      setPublicCompsIndexesByDateParams,
    ]
  );

  return {
    addComparison,
    removeComparison,
    saveMarketAdjustment,
    saveMarketDate,
  };
};

export default useFundInvestmentMarketAdjustmentActions;
